//通用的lodop常用元素构建方法
import { PrintElement, RenderContext } from '../types/PrintElement';
import { PageManager } from '../manager/PageManager';

export class ElementRenderer {
  private pageManager: PageManager;

  constructor(pageManager: PageManager) {
    this.pageManager = pageManager;
  }

  /**
   * 渲染单个打印元素
   * @param element 打印元素
   * @param context 渲染上下文
   * @returns 处理后的元素top值（可能因分页而改变）
   */
  public renderElement(element: PrintElement, context: RenderContext): number {
    const actualTop = this.resolveElementTop(element);
    const placedElement = {
      ...element,
      top: actualTop
    };

    switch (placedElement.type) {
      case 'text':
        this.renderTextElement(placedElement, context);
        break;
      case 'barcode':
        this.renderBarcodeElement(placedElement, context);
        break;
      case 'line':
        this.renderLineElement(placedElement, context);
        break;
      case 'image':
        this.renderImageElement(placedElement, context);
        break;
      case 'html':
        this.renderHtmlElement(placedElement, context);
        break;
    }

    element.top = actualTop;
    return actualTop;
  }

  public renderMultiLineElements(elements: PrintElement[], context: RenderContext): number {
    let lastTop = 0;

    for (const element of elements) {
      lastTop = this.renderElement(element, context);
    }

    return lastTop;
  }

  private resolveElementTop(element: PrintElement): number {
    if (this.pageManager.isFixedElement(element)) {
      return typeof element.top === 'string' ? parseFloat(element.top) : element.top;
    }

    return this.pageManager.placeElement(element);
  }

  private renderTextElement(element: PrintElement, context: RenderContext): void {
    const { top, left, width, height, content, styles, itemType, flag } = element;
    const contentStr = typeof content === 'string' ? content : String(content || '');
    const escapedContent = contentStr
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\n/g, '\\n');

    // 添加文本元素并应用样式
    const addTextWithStyle = (topOffset: number, text: string, customWidth?: string | number) => {
      const finalWidth = customWidth !== undefined ? customWidth : width;
      context.commands.push(`LODOP.ADD_PRINT_TEXT("${topOffset}mm","${left}mm","${finalWidth}","${height}","${text}");`);
      this.applyStyles(styles, context);
      if (itemType !== undefined) {
        context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"ItemType",${itemType});`);
      }
    };

    if (flag === undefined) {
      addTextWithStyle(top, escapedContent);
    } else if (flag < 3) {
      addTextWithStyle(top, escapedContent, '70mm');
    } else if (flag === 3 || flag === 4) {
      if (escapedContent && escapedContent.length > 8) {
        addTextWithStyle(top - 3, escapedContent.substring(0, 8));
        addTextWithStyle(top, escapedContent.substring(8));
      } else {
        addTextWithStyle(top, escapedContent);
      }
    }
  }

  private renderBarcodeElement(element: PrintElement, context: RenderContext): void {
    const { top, left, width, height, content, styles, itemType } = element;
    const contentStr = typeof content === 'string' ? content : String(content || '');
    const escapedContent = contentStr
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\n/g, '\\n');
    context.commands.push(`LODOP.ADD_PRINT_BARCODE("${top}mm","${left}mm",${width},${height},"128AUTO","${escapedContent}");`);
    this.applyStyles(styles, context);
    if (itemType !== undefined) {
      context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"ItemType",${itemType});`);
    }
  }

  private renderLineElement(element: PrintElement, context: RenderContext): void {
    const { content, itemType, flag } = element;
    const [x1, y1, x2, y2, lineWidth, lineStyle] = content.split(',').map(Number);

    if (flag !== undefined && flag < 3) {
      context.commands.push(`LODOP.ADD_PRINT_LINE("${x1}mm", "${y1}mm", "${x2}mm", "${y2 + 10}mm", ${lineWidth}, ${lineStyle});`);
    } else {
      context.commands.push(`LODOP.ADD_PRINT_LINE("${x1}mm", "${y1}mm", "${x2}mm", "${y2}mm", ${lineWidth}, ${lineStyle});`);
    }

    if (itemType !== undefined) {
      context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"ItemType",${itemType});`);
    }
  }

  private renderImageElement(element: PrintElement, context: RenderContext): void {
    const { top, left, width, height, content, styles, itemType, flag } = element;
    context.commands.push(`LODOP.ADD_PRINT_IMAGE("${top}mm","${left}mm","${width}","${height}","${content}");`);
    this.applyStyles(styles, context);
    if (itemType !== undefined && itemType === 1) {
      context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"Stretch",1);`);
    }
    if (itemType !== undefined) {
      context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"ItemType",${itemType});`);
    }
  }

  private renderHtmlElement(element: PrintElement, context: RenderContext): void {
    const { top, left, width, height, content, styles, itemType } = element;
    context.commands.push(`LODOP.ADD_PRINT_HTM("${top}mm", "${left}mm", "${width}", "${height}", \` ${content} \`);`);
    this.applyStyles(styles, context);
    if (itemType !== undefined) {
      context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"ItemType",${itemType});`);
    }
  }

  private applyStyles(styles: any, context: RenderContext): void {
    if (!styles) return;

    for (const [key, value] of Object.entries(styles)) {
      context.commands.push(`LODOP.SET_PRINT_STYLEA(0,"${key}","${value}");`);
    }
  }

  public renderElements(elements: PrintElement[], context: RenderContext): void {
    for (const element of elements) {
      this.renderElement(element, context);
    }
  }
}
